import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import './SubServicePage.css';
import { mainServices, subServicesData, SubService } from '../../data/servicesData';
import { addToCart, addToWishlist } from '../../utils/cartUtils';
import { useCityPricing } from '../../contexts/CityPricingContext';
import SEOHead from '../SEOHead';
import { trackViewItem, trackAddToCart } from '../../utils/analytics';

const SubServicePage: React.FC = () => {
  const { serviceId, subServiceId } = useParams<{ serviceId: string; subServiceId: string }>();
  const location = useLocation();
  const [addingToCart, setAddingToCart] = useState<number | null>(null);
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const highlightedServiceRef = useRef<HTMLDivElement>(null);
  const servicesGridRef = useRef<HTMLElement>(null);
  const { selectedCity, cityPricing, isLoadingPricing } = useCityPricing();
  
  // Helper function to get city-specific price or fallback to default.
  // NOTE: we intentionally do NOT guard on selectedCity here – cityPricing is
  // already hydrated synchronously from the localStorage cache on the first
  // render, so we can use it even before the selectedCity useEffect fires.
  const getCitySpecificPrice = (service: SubService): string => {
    if (cityPricing?.services) {
      const cityService = cityPricing.services.find(s => 
        s.serviceName.toLowerCase().includes(service.name.toLowerCase()) ||
        s.serviceId === service.id
      );
      
      if (cityService && cityService.plans.length > 0) {
        const basePlan = cityService.plans[0]; // Use first plan as default
        return `₹${Math.round(basePlan.totalAmount).toLocaleString()}`; // Show GST-inclusive price
      }
    }
    
    // Fallback to default price
    return service.price;
  };

  // If we have a subServiceId, find the sub-service and its parent service
  let mainService: typeof mainServices[0] | undefined;
  let subServices: SubService[] = [];
  let specificSubService: SubService | undefined;

  if (subServiceId) {
    // Find the specific sub-service across all services
    for (const [parentServiceId, services] of Object.entries(subServicesData)) {
      const foundSubService = services.find(sub => sub.id === parseInt(subServiceId));
      if (foundSubService) {
        specificSubService = foundSubService;
        mainService = mainServices.find(service => service.id === parentServiceId);
        subServices = [foundSubService]; // Show only this specific service
        break;
      }
    }
  } else if (serviceId) {
    // Original logic for service categories
    mainService = mainServices.find(service => service.id === serviceId);
    subServices = serviceId ? subServicesData[serviceId] || [] : [];
  }
  
  // Get highlighted sub-service ID from navigation state
  const highlightSubServiceId = location.state?.highlightSubService;

  // Fire view_item when landing on a specific service page
  useEffect(() => {
    if (specificSubService) {
      const priceStr = getCitySpecificPrice(specificSubService);
      const numericPrice = parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
      trackViewItem({
        item_id: String(specificSubService.id),
        item_name: specificSubService.name,
        item_category: mainService?.name || 'Puja Service',
        price: numericPrice,
        quantity: 1,
      });
    } else if (mainService && subServices.length > 0) {
      // Category page — treat first service as representative
      const first = subServices[0];
      const priceStr = getCitySpecificPrice(first);
      const numericPrice = parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
      trackViewItem({
        item_id: mainService.id,
        item_name: mainService.name,
        item_category: 'Puja Service',
        price: numericPrice,
        quantity: 1,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, subServiceId]);

  // Scroll to top when component mounts or serviceId changes
  useEffect(() => {
    // If there's a highlighted sub-service, scroll to it instead
    if (highlightSubServiceId && highlightedServiceRef.current) {
      setTimeout(() => {
        highlightedServiceRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300);
    } else {
      // Small delay to ensure the page has rendered before scrolling
      const scrollToTop = () => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      };
      
      // Use a small timeout to ensure smooth scrolling after route change
      const timeoutId = setTimeout(scrollToTop, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [serviceId, highlightSubServiceId]);

  const handleAddToCart = (service: SubService, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setAddingToCart(service.id);
    
    const cartItem = {
      id: service.id.toString(),
      name: service.name,
      price: getCitySpecificPrice(service), // Use city-specific pricing instead of static price
      image: '/images/puja_service.jpg',
      category: mainService?.name || 'Puja Services',
      duration: service.duration
    };
    
    addToCart(cartItem);

    // GA4 add_to_cart
    const numericPrice = parseFloat(cartItem.price.replace(/[^0-9.]/g, '')) || 0;
    trackAddToCart({
      item_id: cartItem.id,
      item_name: cartItem.name,
      item_category: cartItem.category,
      price: numericPrice,
      quantity: 1,
    });

    setTimeout(() => {
      setAddingToCart(null);
    }, 1000);
  };

  const handleAddToWishlist = (service: SubService, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const wishlistItem = {
      id: service.id.toString(),
      name: service.name,
      price: service.price,
      image: '/images/puja_service.jpg',
      category: mainService?.name || 'Puja Services',
      duration: service.duration
    };
    
    addToWishlist(wishlistItem);
    alert('Added to wishlist!');
  };

  // Fallback if service not found
  if (!mainService) {
    return (
      <div className="sub-service-page">
        <div className="container">
          <h1>Service not found</h1>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    );
  }
  // Hero section shown on service category pages only (not on individual sub-service pages)
  const heroData = !specificSubService && mainService?.heroTitle
    ? mainService
    : null;

  // ── SEO helpers ──────────────────────────────────────────────────────────
  const seoTitle = specificSubService
    ? `${specificSubService.name} – Book with Kashi Acharyas`
    : `${mainService.name} – Authentic Vedic Ceremonies by Kashi Acharyas`;

  const seoDescription = specificSubService
    ? `Book ${specificSubService.name} with Kashi-certified Acharyas. ${specificSubService.description}. Starting ${specificSubService.price}. Duration: ${specificSubService.duration}.`
    : `${mainService.description} – Book authentic ${mainService.name} ceremonies with Kashi Acharyas. ${mainService.priceRange}.`;

  const seoCanonicalPath = specificSubService
    ? `/sub-service/${specificSubService.id}`
    : `/service/${serviceId}`;

  const seoKeywords = specificSubService
    ? `${specificSubService.name}, book ${specificSubService.name} online, ${mainService.name}, puja booking`
    : `${mainService.name}, ${mainService.category}, puja booking India`;

  const serviceStructuredData = specificSubService
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": specificSubService.name,
        "description": specificSubService.description,
        "provider": {
          "@type": "Organization",
          "name": "Antaryatra",
          "url": "https://www.antaryatra.org"
        },
        "offers": {
          "@type": "Offer",
          "price": specificSubService.price.replace(/[₹,]/g, ''),
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock"
        },
        "areaServed": "India",
        "serviceType": "Vedic Puja Ceremony"
      }
    : {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": mainService.name,
        "description": mainService.description,
        "provider": {
          "@type": "Organization",
          "name": "Antaryatra",
          "url": "https://www.antaryatra.org"
        },
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": mainService.priceRange.split(' - ')[0].replace(/[₹,]/g, ''),
          "highPrice": mainService.priceRange.split(' - ')[1]?.replace(/[₹,]/g, '') || '',
          "priceCurrency": "INR"
        },
        "areaServed": "India",
        "serviceType": "Vedic Puja Ceremony",
        "category": mainService.category
      };

  return (
    <div className="sub-service-page">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalPath={seoCanonicalPath}
        keywords={seoKeywords}
        ogType="website"
        structuredData={serviceStructuredData}
      />
      {/* Announcement Banner */}
      {announcementVisible && heroData?.announcementText && (
        <div className="hero-announcement-banner">
          <span className="announcement-icon">🔔</span>
          <span className="announcement-text">{heroData.announcementText}</span>
          <button
            className="announcement-close"
            onClick={() => setAnnouncementVisible(false)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className={`sub-service-hero${heroData ? ' hero-split' : ''}`}>
        {heroData ? (
          /* ── Split layout (hero data available) ── */
          <>
            {/* Left: content */}
            <div className="hero-left">
              {/* Breadcrumb */}
              <nav className="breadcrumb breadcrumb-dark">
                <Link to="/">Home</Link>
                <span>›</span>
                {specificSubService ? (
                  <>
                    <Link to={`/service/${Object.entries(subServicesData).find(([_, services]) =>
                      services.some(s => s.id === specificSubService?.id))?.[0]}`}>
                      {mainService.name}
                    </Link>
                    <span>›</span>
                    <span>{specificSubService.name}</span>
                  </>
                ) : (
                  <span>{mainService.name}</span>
                )}
              </nav>

              {heroData.heroTagline && (
                <p className="hero-tagline">{heroData.heroTagline}</p>
              )}

              <h1 className="hero-title-split">{heroData.heroTitle}</h1>

              <p className="hero-desc-split">
                {heroData.heroDescription || heroData.description}
              </p>

              {/* Rating row */}
              <div className="hero-rating-row">
                <span className="hero-stars">
                  {[...Array(5)].map((_, i) => {
                    const rating = heroData.heroRating ?? 5;
                    const filled = i < Math.floor(rating);
                    const half = !filled && i < rating;
                    return (
                      <svg key={i} className={`hero-star${filled ? ' filled' : half ? ' half' : ''}`} viewBox="0 0 24 24">
                        <defs>
                          <linearGradient id={`half-${i}`}>
                            <stop offset="50%" stopColor="#fbbf24" />
                            <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                          </linearGradient>
                        </defs>
                        <path
                          fill={filled ? '#fbbf24' : half ? `url(#half-${i})` : 'rgba(255,255,255,0.3)'}
                          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        />
                      </svg>
                    );
                  })}
                </span>
                <span className="hero-rating-value">
                  {(heroData.heroRating ?? 5).toFixed(1)}
                </span>
                {heroData.familiesServed && (
                  <>
                    <span className="hero-divider">|</span>
                    <span className="hero-families">{heroData.familiesServed} Families Served</span>
                  </>
                )}
              </div>

              {/* Service modes */}
              {heroData.serviceModes && (
                <div className="hero-modes">
                  {heroData.serviceModes.map((mode, i) => (
                    <span key={i} className="hero-mode-badge">{mode}</span>
                  ))}
                </div>
              )}

              {/* Starting price */}
              <div className="hero-starting-price">
                Starting{' '}
                <span className="hero-price-value">
                  {isLoadingPricing
                    ? <span className="price-skeleton hero-price-skeleton"></span>
                    : (heroData.startingPrice || (specificSubService ? getCitySpecificPrice(specificSubService) : mainService.priceRange))
                  }
                </span>
                {!isLoadingPricing && cityPricing && specificSubService && (
                  <small className="city-price-indicator"> for {cityPricing.cityName}</small>
                )}
              </div>

              {/* CTA buttons */}
              <div className="hero-ctas">
                {specificSubService ? (
                  <Link
                    to={`/booking/${serviceId}/${specificSubService.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="hero-cta-primary"
                  >
                    🔶 Choose My {specificSubService.name.split(' ')[0]} Plan →
                  </Link>
                ) : (
                  <button
                    className="hero-cta-primary"
                    onClick={() =>
                      servicesGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  >
                    🔶 Explore {mainService.name} Plans →
                  </button>
                )}
                <a href="tel:+919217588609" className="hero-cta-secondary">
                  📞 Speak to Specialist
                </a>
              </div>
            </div>

            {/* Right: image */}
            <div className="hero-right">
              <img
                src={specificSubService ? specificSubService.image : mainService.image}
                alt={specificSubService ? specificSubService.name : mainService.name}
                className="hero-image-split"
              />
              <div className="hero-image-overlay-split" />
            </div>
          </>
        ) : (
          /* ── Original centred layout (no hero data) ── */
          <div className="hero-image-container">
            <img
              src={specificSubService ? specificSubService.image : mainService.image}
              alt={specificSubService ? specificSubService.name : mainService.name}
              className="hero-image"
            />
            <div className="hero-overlay">
              <div className="hero-content">
                {specificSubService ? (
                  <>
                    <nav className="breadcrumb">
                      <Link to="/">Home</Link>
                      <span>›</span>
                      <Link to={`/service/${Object.entries(subServicesData).find(([_, services]) =>
                        services.some(s => s.id === specificSubService?.id))?.[0]}`}>
                        {mainService.name}
                      </Link>
                      <span>›</span>
                      <span>{specificSubService.name}</span>
                    </nav>
                    <h1>{specificSubService.name}</h1>
                    <p>{specificSubService.description}</p>
                    <div className="service-details-hero">
                      <span className="price">
                        {isLoadingPricing
                          ? <span className="price-skeleton hero-price-skeleton"></span>
                          : getCitySpecificPrice(specificSubService)
                        }
                        {!isLoadingPricing && cityPricing && (
                          <small className="city-price-indicator">
                            for {cityPricing.cityName}
                          </small>
                        )}
                      </span>
                      <span className="duration">{specificSubService.duration}</span>
                      <span className="rating">★ {specificSubService.rating}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <nav className="breadcrumb">
                      <Link to="/">Home</Link>
                      <span>›</span>
                      <span>{mainService.name}</span>
                    </nav>
                    <h1>{mainService.name}</h1>
                    <p>{mainService.description}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Sub Services Grid */}
      <section className="sub-services-section" ref={servicesGridRef}>
        <div className="container">
          {!specificSubService && (
            <div className="section-header">
              <h2>Choose Your Pujan</h2>
              <p>Select from our range of premium {mainService.name.toLowerCase()} options</p>
            </div>
          )}

          {specificSubService && (
            <div className="section-header">
              <h2>Service Details</h2>
              <p>Complete information about {specificSubService.name}</p>
            </div>
          )}

          {/* Specialist Consultation Banner */}
          {/* <div className="specialist-consultation-banner">
            <div className="consultation-content">
              <div className="consultation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 0 0-1.02.24l-2.2 2.2a15.045 15.045 0 0 1-6.59-6.59l2.2-2.2c.27-.27.35-.67.24-1.02A11.36 11.36 0 0 1 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1zM5.03 5h1.5c.07.88.22 1.75.45 2.58l-1.2 1.2c-.41-1.21-.66-2.47-.75-3.78zM19 18.97c-1.32-.09-2.6-.35-3.8-.76l1.2-1.2c.85.24 1.72.39 2.6.45v1.51z"/>
                </svg>
              </div>
              <div className="consultation-text">
                <h3>Need Personalized Assistance?</h3>
                <p>Connect with Antaryatra specialist for personalized guidance and booking assistance</p>
              </div>
              <div className="consultation-actions">
                <a 
                  href="tel:1800-123-4567" 
                  className="toll-free-btn"
                  onClick={() => {
                    // Track the call attempt (optional analytics)
                    console.log('User attempted to call specialist');
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  Call Toll-Free: 1800-123-4567
                </a>
                <div className="consultation-hours">
                  <small>Available 9 AM - 9 PM | 7 Days a Week</small>
                </div>
              </div>
            </div>
          </div> */}
          
          <div className="sub-services-grid">
            {subServices.map((service) => (
              <div 
                key={service.id} 
                className={`sub-service-card ${highlightSubServiceId === service.id ? 'highlighted' : ''}`}
                ref={highlightSubServiceId === service.id ? highlightedServiceRef : null}
              >
                <div className="service-icon-container">
                  {service.icon}
                </div>
                
                <div className="service-info">
                  <h3>{service.name}</h3>
                  <p className="service-description">{service.description}</p>
                  
                  <div className="service-meta">
                    <div className="price-duration">
                      <span className="price">
                        {isLoadingPricing
                          ? <span className="price-skeleton card-price-skeleton"></span>
                          : getCitySpecificPrice(service)
                        }
                        {!isLoadingPricing && cityPricing && (
                          <small className="city-price-indicator">
                            for {cityPricing.cityName}
                          </small>
                        )}
                      </span>
                      <span className="duration">{service.duration}</span>
                    </div>
                    
                    {/* <div className="rating">
                      <div className="stars">
                        {[...Array(5)].map((_, index) => (
                          <svg
                            key={index}
                            className={`star ${index < service.rating ? 'filled' : ''}`}
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                        ))}
                      </div>
                      <span className="rating-text">{service.rating}.0</span>
                    </div> */}
                  </div>
                  
                  <div className="service-actions">
                    <Link 
                      to={`/booking/${serviceId}/${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="book-now-btn primary"
                    >
                      Plan a pooja
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                      </svg>
                    </Link>
                    
                    <div className="secondary-service-actions">
                      <button 
                        className={`add-to-cart-btn ${addingToCart === service.id ? 'adding' : ''}`}
                        onClick={(e) => handleAddToCart(service, e)}
                        disabled={addingToCart === service.id}
                      >
                        {addingToCart === service.id ? (
                          <>
                            <span className="spinner"></span>
                            Added!
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7 18C5.9 18 5.01 18.9 5.01 20S5.9 22 7 22 8.99 21.1 8.99 20 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.24 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.58 17.3 11.97L20.88 5H5.21L4.27 3H1V2H1ZM17 18C15.9 18 15.01 18.9 15.01 20S15.9 22 17 22 18.99 21.1 18.99 20 18.1 18 17 18Z" fill="currentColor"/>
                            </svg>
                            Add Pooja
                          </>
                        )}
                      </button>
                      
                      <button 
                        className="add-to-wishlist-btn"
                        onClick={(e) => handleAddToWishlist(service, e)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Wishlist
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3>Certified Professionals</h3>
              <p>All our service providers are certified and experienced</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                </svg>
              </div>
              <h3>Full Satisfaction</h3>
              <p>Dignity in practice. Integrity in execution</p>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                </svg>
              </div>
              <h3>Flexible Scheduling</h3>
              <p>Book appointments that fit your busy schedule</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubServicePage;